import { useGradingCompanies } from "@/hooks/useGradingCompanies";
import { useGradesQuery } from "@/queries/useGradesQuery";
import { Tables } from "@/integrations/supabase/types";
import { getCardTruePrice } from "@/api/market";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/spinner";
import { formatPrice } from "@/utils/formatPrice";
interface Props {
  cardId: number;
  selectedVariant:
    | (Tables<"variants"> & {
        condition?: { name: string | null };
        printing?: { name: string | null };
      })
    | null;
  gradingCompanyId: string | null;
  gradeId: string | null;
  quantity: number;
  labelTier?: "standard" | "pristine" | "black_label";
  pricingEligible?: boolean;
  language?: "english" | "japanese" | "chinese";
  chineseScript?: "simplified" | "traditional";
}
export function AddCardToBinderTotalPrice({
  cardId,
  selectedVariant,
  gradingCompanyId,
  gradeId,
  quantity,
  pricingEligible = true,
  language = "english",
  chineseScript,
  labelTier,
}: Props) {
  const graded = Boolean(gradingCompanyId && gradeId);
  const { data: companies } = useGradingCompanies();
  const { data: grades } = useGradesQuery();
  const company = companies?.data.find((row) => row.id === gradingCompanyId);
  const grade = grades?.find((row) => row.id === gradeId);
  const options = {
    cardFormat:
      gradingCompanyId && gradeId ? ("graded" as const) : ("raw" as const),
    language,
    chineseScript,
    condition: graded
      ? undefined
      : (selectedVariant?.condition?.name ?? undefined),
    printing: selectedVariant?.printing?.name ?? undefined,
    gradingCompany: graded ? (company?.name ?? undefined) : undefined,
    grade: graded ? (grade?.id ?? undefined) : undefined,
    labelTier: graded ? labelTier : undefined,
  };
  const { data, isFetching } = useQuery({
    queryKey: ["exact-binder-price", cardId, options],
    queryFn: () => getCardTruePrice(cardId, options),
    enabled: Boolean(
      selectedVariant && pricingEligible && (!graded || (company && grade)),
    ),
  });
  if (isFetching) return <Spinner className="w-6 h-6" />;
  const value = pricingEligible ? data?.data.truePrice : null;
  return (
    <div className="text-lg">
      {value != null ? (
        <>
          <span className="text-[#16a34a]">
            {formatPrice(value * quantity)}
          </span>
          <p className="text-xs text-muted-foreground">
            Exact-match market estimate · {options.printing}
          </p>
        </>
      ) : (
        <span className="text-muted-foreground">
          Exact market price unavailable
        </span>
      )}
    </div>
  );
}
